import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Gallery from './Gallery';

// Import Local Video
import homeVideoDesktop from '../assets/Home/0812.mp4'; // Desktop video
import homeVideoMobile from '../assets/Home/0813.mp4'; // Mobile video

// Import Villa & Spa Images
import villaImage1 from '../assets/Home/WhatsApp Image 2025-08-14 at 10.16.15.jpeg';
import villaImage2 from '../assets/Home/WhatsApp Image 2025-08-14 at 10.16.16.jpeg';

// Import Apartment Images
import apartmentImage1 from '../assets/Home/WhatsApp Image 2025-08-14 at 10.23.14.jpeg';
import apartmentImage2 from '../assets/Home/WhatsApp Image 2025-08-14 at 10.23.13.jpeg';

import spaImage from '../assets/Home/WhatsApp Image 2025-08-14 at 10.25.55.jpeg';
import villaImage from '../assets/Home/Untitled design.png';

import contactBackgroundImage from '../assets/Home/s10.jpeg';

const Home = () => {
    const { t } = useTranslation();

    // Load Elfsight script for Google Reviews
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://static.elfsight.com/platform/platform.js';
        script.async = true;
        script.defer = true; // Added defer for better loading
        document.body.appendChild(script);

        // Cleanup script on component unmount
        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    return (
        <>
            {/* Video Section */}
            <section className="relative flex items-center justify-center text-center w-full h-[85vh] overflow-hidden">
                {/* Desktop Video (hidden on sm) */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute w-full h-full object-cover hidden md:block"
                >
                    <source src={homeVideoDesktop} type="video/mp4" />
                    {t('home.videoSection.fallback')}
                </video>
                {/* Mobile Video (hidden on md and larger) */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute w-full h-full object-cover block md:hidden"
                >
                    <source src={homeVideoMobile} type="video/mp4" />
                    {t('home.videoSection.fallback')}
                </video>
                <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50"></div>
                <div className="relative text-white z-10">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">{t('home.videoSection.title')}</h1>
                    <p className="text-base sm:text-lg md:text-xl w-11/12 sm:w-3/4 mx-auto mt-4">
                        {t('home.videoSection.description')}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-20 mt-8 sm:mt-32">
                        <div className="flex flex-col items-center">
                            <svg
                                className="w-10 h-10 sm:w-12 sm:h-12 text-[#E6A71A] mb-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-14m14 0H9m0 0H5m4 0h6m-9-7h12m-12 4h12"
                                />
                            </svg>
                            <p className="font-semibold text-sm sm:text-base">{t('home.videoSection.resortLabel')}</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <svg
                                className="w-10 h-10 sm:w-12 sm:h-12 text-[#E6A71A] mb-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M3 12l2-2m0 0l7-7 7 7m-9 2v6a2 2 0 002 2h4a2 2 0 002-2v-6m-9 0h6"
                                />
                            </svg>
                            <p className="font-semibold text-sm sm:text-base">{t('home.videoSection.ayurvedaLabel')}</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <svg
                                className="w-10 h-10 sm:w-12 sm:h-12 text-[#E6A71A] mb-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5v-2a2 2 0 012-2h10a2 2 0 012 2v2h-4m-6 0h6"
                                />
                            </svg>
                            <p className="font-semibold text-sm sm:text-base">{t('home.videoSection.restaurantLabel')}</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <svg
                                className="w-10 h-10 sm:w-12 sm:h-12 text-[#E6A71A] mb-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 4v16m8-12H4m4 4h8"
                                />
                            </svg>
                            <p className="font-semibold text-sm sm:text-base">{t('home.videoSection.beachLabel')}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hero Section */}
            <div className="text-center mb-4 mt-8 sm:mt-12 md:mt-16 px-4">
                <h5 className="text-[#E6A71A] text-base sm:text-lg md:text-xl">
                    {t('home.heroSection.subtitle')}
                </h5>
                <h1 className="font-bold text-[#114131] text-3xl sm:text-4xl md:text-[3.5rem]">
                    {t('home.heroSection.title')}
                </h1>
                <p className="text-center mx-auto mt-6 sm:mt-8 md:mt-10 mb-6 sm:mb-8 md:mb-10 text-gray-600 max-w-[90%] sm:max-w-[80%] md:max-w-[1500px] text-base sm:text-lg md:text-[1.5em]">
                    {t('home.heroSection.description')}
                </p>
            </div>

            {/* Exclusive Benefits Section */}
            <section className="max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-10 text-center">
                <h3 className="text-[#E6A71A] text-base sm:text-lg md:text-xl">
                    {t('home.benefitsSection.subtitle')}
                </h3>
                <h2 className="font-bold text-[#114131] text-2xl sm:text-3xl md:text-[3.5rem]">
                    {t('home.benefitsSection.title')}
                </h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap justify-around items-center gap-4 sm:gap-6 md:gap-6 mt-6 sm:mt-8 md:mt-10">
                    <div className="flex flex-col items-center w-full sm:w-1/2 md:w-auto">
                        <svg
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 text-[#E6A71A] mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 9V7a4 4 0 00-8 0v2M7 13v2a4 4 0 008 0v-2M7 13h10M7 13a4 4 0 01-4-4v4a4 4 0 004 4m10-4v4a4 4 0 01-4 4m0-12v12"
                            ></path>
                        </svg>
                        <p className="font-semibold text-sm sm:text-base md:text-base">
                            {t('home.benefitsSection.benefit1')}
                        </p>
                    </div>
                    <div className="flex flex-col items-center w-full sm:w-1/2 md:w-auto">
                        <svg
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 text-[#E6A71A] mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 114 0 2 2 0 01-4 0z"
                            ></path>
                        </svg>
                        <p className="font-semibold text-sm sm:text-base md:text-base">
                            {t('home.benefitsSection.benefit2')}
                        </p>
                    </div>
                    <div className="flex flex-col items-center w-full sm:w-1/2 md:w-auto">
                        <svg
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 text-[#E6A71A] mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 17a4 4 0 018 0m0 0a4 4 0 008 0M12 3v4m4-2l-4 4-4-4m4 10v6"
                            />
                        </svg>
                        <p className="font-semibold text-sm sm:text-base md:text-base">
                            {t('home.benefitsSection.benefit3')}
                        </p>
                    </div>
                    <div className="flex flex-col items-center w-full sm:w-1/2 md:w-auto">
                        <svg
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 text-[#E6A71A] mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M7 9v11H2v-11m14 0v11m0-11h-3m-4 0H7m6 0v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v3"
                            ></path>
                        </svg>
                        <p className="font-semibold text-sm sm:text-base md:text-base">
                            {t('home.benefitsSection.benefit4')}
                        </p>
                    </div>
                </div>
                <a
                    href="/packages"
                    className="mt-6 sm:mt-8 inline-block bg-teal-900 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-md font-semibold text-sm sm:text-base hover:bg-yellow-500 transition-colors duration-300"
                >
                    {t('home.benefitsSection.bookNow')}
                </a>
            </section>

            {/* Sun Flower Beach Resort & Ayurveda Spa Section - Desktop (md and larger) */}
            <section className="max-w-7xl mx-auto px-4 my-5 hidden md:block mb-10">
                <div className="flex flex-col md:flex-row items-center mb-10">
                    <div className="w-full md:w-3/5">
                        <h2 className="font-bold text-[#114131] text-2xl sm:text-3xl md:text-[3.5rem]">
                            {t('home.villaSpaSection.title')}
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-4">
                            {t('home.villaSpaSection.description1')}
                        </p>
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-4">
                            {t('home.villaSpaSection.description2')}
                        </p>
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-4">
                            {t('home.villaSpaSection.description3')}
                        </p>
                    </div>
                    <div className="w-full md:w-2/5 relative mt-4 md:ml-16 mb-20">
                        <div className="relative">
                            <img
                                src={villaImage1}
                                alt={t('home.villaSpaSection.imageAlt1')}
                                className="w-full md:w-[90%] rounded-xl shadow-lg"
                            />
                            <img
                                src={villaImage2}
                                alt={t('home.villaSpaSection.imageAlt2')}
                                className="absolute bottom-[-100px] left-[40%] w-[65%] rounded-xl shadow-lg "
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Sun Flower Beach Resort & Ayurveda Spa Section - Mobile (sm) */}
            <section className="max-w-7xl mx-auto px-4 my-5 block md:hidden text-center">
                <h2 className="font-bold text-[#114131] text-3xl sm:text-3xl">
                    {t('home.villaSpaSection.title')}
                </h2>
                <div className="flex flex-col items-center mt-4 gap-4">
                    <img
                        src={villaImage1}
                        alt={t('home.villaSpaSection.imageAlt1')}
                        className="w-full max-w-[400px] rounded-xl shadow-lg"
                    />
                    <img
                        src={villaImage2}
                        alt={t('home.villaSpaSection.imageAlt2')}
                        className="w-full max-w-[400px] rounded-xl shadow-lg"
                    />
                </div>
                <div className="mt-4">
                    <p className="text-base sm:text-lg text-gray-500">
                        {t('home.villaSpaSection.description1')}
                    </p>
                    <p className="text-base sm:text-lg text-gray-500 mt-4">
                        {t('home.villaSpaSection.description2')}
                    </p>
                    <p className="text-base sm:text-lg text-gray-500 mt-4">
                        {t('home.villaSpaSection.description3')}
                    </p>
                </div>
            </section>

            {/* Luxury Apartments Section - Desktop (md and larger) */}
            <section className="max-w-7xl mx-auto px-4 my-5 hidden md:block mt-20">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="md:w-1/2">
                        <div className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-transform duration-400 ease-in-out hover:scale-103">
                            <img
                                src={apartmentImage1}
                                alt={t('home.apartmentsSection.imageAlt1')}
                                className="w-full h-[400px] object-cover brightness-70 transition-all duration-400 ease-in-out hover:brightness-[85%]"
                            />
                            <div className="absolute top-0 left-0 w-full h-full bg-black/50 transition-all duration-400 ease-in-out hover:bg-white/[0.2]"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center w-[85%] z-10 transition-all duration-400 ease-in-out hover:text-black">
                                <h2 className="font-bold uppercase text-2xl md:text-3xl">{t('home.apartmentsSection.title1')}</h2>
                                <p className="mt-2 text-base md:text-xl">{t('home.apartmentsSection.description1')}</p>
                                <a
                                    href="/deluxe-double-rooms"
                                    className="inline-block bg-white text-black py-2 md:py-3 px-4 md:px-6 mt-3 rounded-full font-bold text-sm md:text-lg transition-all duration-400 ease-in-out hover:bg-black hover:text-white hover:border hover:border-white"
                                >
                                    {t('home.apartmentsSection.viewMore')}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="md:w-1/2">
                        <div className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-transform duration-400 ease-in-out hover:scale-103">
                            <img
                                src={apartmentImage2}
                                alt={t('home.apartmentsSection.imageAlt2')}
                                className="w-full h-[400px] object-cover brightness-70 transition-all duration-400 ease-in-out hover:brightness-[85%]"
                            />
                            <div className="absolute top-0 left-0 w-full h-full bg-black/50 transition-all duration-400 ease-in-out hover:bg-white/[0.2]"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center w-[85%] z-10 transition-all duration-400 ease-in-out hover:text-black">
                                <h2 className="font-bold uppercase text-2xl md:text-3xl">{t('home.apartmentsSection.title2')}</h2>
                                <p className="mt-2 text-base md:text-xl">{t('home.apartmentsSection.description2')}</p>
                                <a
                                    href="/deluxe-double-room-sea-view"
                                    className="inline-block bg-white text-black py-2 md:py-3 px-4 md:px-6 mt-3 rounded-full font-bold text-sm md:text-lg transition-all duration-400 ease-in-out hover:bg-black hover:text-white hover:border hover:border-white"
                                >
                                    {t('home.apartmentsSection.viewMore')}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Luxury Apartments Section - Mobile (sm) */}
            <section className="max-w-7xl mx-auto px-4 my-5 block md:hidden text-center">

                <div className="flex flex-col gap-4">
                    <div className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-transform duration-400 ease-in-out hover:scale-103">
                        <img
                            src={apartmentImage1}
                            alt={t('home.apartmentsSection.imageAlt1')}
                            className="w-full h-[250px] sm:h-[300px] object-cover brightness-70 transition-all duration-400 ease-in-out hover:brightness-[85%]"
                        />
                        <div className="absolute top-0 left-0 w-full h-full bg-black/50 transition-all duration-400 ease-in-out hover:bg-white/[0.2]"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center w-[85%] z-10 transition-all duration-400 ease-in-out hover:text-black">
                            <h2 className="font-bold uppercase text-xl sm:text-2xl">{t('home.apartmentsSection.title1')}</h2>
                            <p className="mt-2 text-sm sm:text-base">{t('home.apartmentsSection.description1')}</p>
                            <a
                                href="/deluxe-double-rooms"
                                className="inline-block bg-white text-black py-2 px-4 mt-3 rounded-full font-bold text-sm sm:text-base transition-all duration-400 ease-in-out hover:bg-black hover:text-white hover:border hover:border-white"
                            >
                                {t('home.apartmentsSection.viewMore')}
                            </a>
                        </div>
                    </div>
                    <div className="relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-transform duration-400 ease-in-out hover:scale-103">
                        <img
                            src={apartmentImage2}
                            alt={t('home.apartmentsSection.imageAlt2')}
                            className="w-full h-[250px] sm:h-[300px] object-cover brightness-70 transition-all duration-400 ease-in-out hover:brightness-[85%]"
                        />
                        <div className="absolute top-0 left-0 w-full h-full bg-black/50 transition-all duration-400 ease-in-out hover:bg-white/[0.2]"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center w-[85%] z-10 transition-all duration-400 ease-in-out hover:text-black">
                            <h2 className="font-bold uppercase text-xl sm:text-2xl">{t('home.apartmentsSection.title2')}</h2>
                            <p className="mt-2 text-sm sm:text-base">{t('home.apartmentsSection.description2')}</p>
                            <a
                                href="/deluxe-double-room-sea-view"
                                className="inline-block bg-white text-black py-2 px-4 mt-3 rounded-full font-bold text-sm sm:text-base transition-all duration-400 ease-in-out hover:bg-black hover:text-white hover:border hover:border-white"
                            >
                                {t('home.apartmentsSection.viewMore')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ayurveda Wellness Section - Desktop (md and larger) */}
            <section className="max-w-7xl mx-auto px-4 my-5 hidden md:block">
                <h2 className="text-center font-bold text-[#114131] text-2xl sm:text-3xl md:text-[3.5rem] mb-4">
                    {t('home.ayurvedaSection.title')}
                </h2>
                <div className="flex flex-col md:flex-row items-center">
                    <div className="md:w-1/2">
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-6 md:mt-10">
                            {t('home.ayurvedaSection.description1')}
                        </p>
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-6 md:mt-10">
                            {t('home.ayurvedaSection.description2')}
                        </p>
                        <a
                            href="/ayurveda-spa"
                            className="inline-block bg-teal-900 text-white py-2 sm:py-3 px-4 sm:px-6 mt-3 rounded-full font-bold text-sm sm:text-base md:text-base transition-all duration-400 ease-in-out hover:bg-yellow-500 hover:text-white hover:border hover:border-white"
                        >
                            {t('home.ayurvedaSection.viewTreatments')}
                        </a>
                    </div>
                    <div className="md:w-1/2 text-center mt-4 md:mt-0 md:ml-4">
                        <div className="relative overflow-hidden rounded-xl shadow-lg transition-transform duration-400 ease-in-out hover:scale-105">
                            <img
                                src={spaImage}
                                alt={t('home.ayurvedaSection.imageAlt')}
                                className="w-full h-[300px] sm:h-[400px] md:h-[500px] rounded-xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Ayurveda Wellness Section - Mobile (sm) */}
            <section className="max-w-7xl mx-auto px-4 my-5 block md:hidden text-center">
                <h2 className="font-bold text-[#114131] text-3xl sm:text-3xl mb-4">
                    {t('home.ayurvedaSection.title')}
                </h2>
                <div className="flex flex-col items-center">
                    <div className="relative overflow-hidden rounded-xl shadow-lg transition-transform duration-400 ease-in-out hover:scale-105">
                        <img
                            src={spaImage}
                            alt={t('home.ayurvedaSection.imageAlt')}
                            className="w-full max-w-[400px] h-[250px] sm:h-[300px] rounded-xl object-cover"
                        />
                    </div>
                    <div className="mt-4">
                        <p className="text-base sm:text-lg text-gray-500">
                            {t('home.ayurvedaSection.description1')}
                        </p>
                        <p className="text-base sm:text-lg text-gray-500 mt-4">
                            {t('home.ayurvedaSection.description2')}
                        </p>
                        <a
                            href="/ayurveda-spa"
                            className="inline-block bg-teal-900 text-white py-2 sm:py-3 px-4 sm:px-6 mt-4 rounded-full font-bold text-sm sm:text-base transition-all duration-400 ease-in-out hover:bg-yellow-500 hover:text-white hover:border hover:border-white"
                        >
                            {t('home.ayurvedaSection.viewTreatments')}
                        </a>
                    </div>
                </div>
            </section>

            {/* Sun Flower Beach Resort Section - Desktop (md and larger) */}
            <section className="max-w-7xl mx-auto px-4 my-5 hidden md:block">
                <h2 className="text-center font-bold text-[#114131] text-2xl sm:text-3xl md:text-[3.5rem] mb-4 md:mb-10">
                    {t('home.villaSection.title')}
                </h2>
                <div className="flex flex-col md:flex-row items-center">
                    <div className="md:w-1/2 text-center">
                        <div className="relative overflow-hidden rounded-xl shadow-lg transition-transform duration-400 ease-in-out hover:scale-105">
                            <img
                                src={villaImage}
                                alt={t('home.villaSection.imageAlt')}
                                className="w-auto h-auto sm:h-[400px] md:h-[500px] rounded-xl object-cover"
                            />
                        </div>
                    </div>
                    <div className="md:w-1/2 mt-4 md:mt-0 md:ml-10">
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-6 md:mt-4">
                            {t('home.villaSection.description1')}
                        </p>
                        <p className="text-base sm:text-lg md:text-xl text-gray-500 mt-6 md:mt-32">
                            {t('home.villaSection.description2')}
                        </p>
                        <a
                            href="/villa"
                            className="inline-block bg-teal-900 text-white py-2 sm:py-3 px-4 sm:px-6 mt-10 md:mt-6 rounded-full font-bold text-sm sm:text-base md:text-base transition-all duration-400 ease-in-out hover:bg-yellow-500 hover:text-white hover:border hover:border-white"
                        >
                            {t('home.villaSection.viewRooms')}
                        </a>
                    </div>
                </div>
            </section>

            {/* Sun Flower Beach Resort Section - Mobile (sm) */}
            <section className="max-w-7xl mx-auto px-4 my-5 block md:hidden text-center">
                <h2 className="font-bold text-[#114131] text-3xl sm:text-3xl mb-4">
                    {t('home.villaSection.title')}
                </h2>
                <div className="flex flex-col items-center">
                    <div className="relative overflow-hidden rounded-xl shadow-lg transition-transform duration-400 ease-in-out hover:scale-105">
                        <img
                            src={villaImage}
                            alt={t('home.villaSection.imageAlt')}
                            className="w-auto  h-[300px] sm:h-[300px] rounded-xl object-cover"
                        />
                    </div>
                    <div className="mt-4">
                        <p className="text-base sm:text-lg text-gray-500">
                            {t('home.villaSection.description1')}
                        </p>
                        <p className="text-base sm:text-lg text-gray-500 mt-4">
                            {t('home.villaSection.description2')}
                        </p>
                        <a
                            href="/villa"
                            className="inline-block bg-teal-900 text-white py-2 sm:py-3 px-4 sm:px-6 mt-4 rounded-full font-bold text-sm sm:text-base transition-all duration-400 ease-in-out hover:bg-yellow-500 hover:text-white hover:border hover:border-white"
                        >
                            {t('home.villaSection.viewRooms')}
                        </a>
                    </div>
                </div>
            </section>

            {/* Google Reviews Section */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 my-5 sm:my-8 md:my-10">
                <h2 className="text-center font-bold text-[#114131] text-3xl sm:text-3xl md:text-[3.5rem] mb-4 sm:mb-6 md:mb-10">
                    {t('home.reviewsSection.title')}
                </h2>
                <div
                    className="elfsight-app-157cc4e7-89b1-4eed-8e2f-57b788077fa8 w-full min-h-[300px]"
                    data-elfsight-app-lazy
                >
                    <div className="text-center text-gray-500">Loading reviews...</div>
                </div>
            </section>

            {/* Our Gallery Section */}
            <Gallery />

            {/* Contact Us Section */}
            <section
                className="relative bg-cover bg-center py-12 sm:py-16 md:py-24 flex flex-col items-center justify-center text-center text-white h-[50vh]"
                style={{ backgroundImage: `url(${contactBackgroundImage})` }}
            >
                <div className="absolute top-0 left-0 w-full h-full bg-black/50"></div>
                <div className="relative z-10 w-full max-w-[90%] sm:max-w-[80%] md:max-w-[900px] px-4">
                    <h2 className="font-bold text-2xl sm:text-3xl md:text-5xl mb-2 sm:mb-3 md:mb-3">
                        {t('home.contactSection.title')}
                    </h2>
                    <p className="text-base sm:text-lg md:text-xl w-full sm:w-3/4 mx-auto mb-4 sm:mb-6">
                        {t('home.contactSection.description')}
                    </p>
                    <a
                        href="/contact-us"
                        className="inline-block bg-transparent border-2 border-white text-white py-2 sm:py-3 px-4 sm:px-6 rounded-full font-bold text-sm sm:text-base md:text-base transition-all duration-400 ease-in-out hover:bg-white hover:text-black"
                    >
                        {t('home.contactSection.contactUs')}
                    </a>
                </div>
            </section>
        </>
    );
};

export default Home;